import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, Search } from 'lucide-react';
import { format } from 'date-fns';

interface ErrorLogsTableProps {
  logs: any[];
  isLoading: boolean;
}

export function ErrorLogsTable({ logs, isLoading }: ErrorLogsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('all');

  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.event_message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'all' || log.error_severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'FATAL':
      case 'PANIC':
        return <Badge variant="destructive">CRITICAL</Badge>;
      case 'ERROR':
        return <Badge variant="destructive">ERROR</Badge>;
      case 'WARNING':
        return <Badge variant="warning">WARNING</Badge>;
      case 'LOG':
      case 'INFO':
        return <Badge variant="secondary">INFO</Badge>;
      default:
        return <Badge variant="outline">{severity}</Badge>;
    }
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading logs...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Error Logs</CardTitle>
        <CardDescription>Database errors and warnings from the last 100 log entries</CardDescription>
        <div className="flex gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="FATAL">Critical Only</SelectItem>
              <SelectItem value="ERROR">Errors Only</SelectItem>
              <SelectItem value="WARNING">Warnings Only</SelectItem>
              <SelectItem value="LOG">Info Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead className="w-[120px]">Severity</TableHead>
                <TableHead>Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground">
                    No logs found
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <Collapsible key={log.id} asChild>
                    <>
                      <TableRow className="cursor-pointer">
                        <TableCell className="font-mono text-sm">
                          {format(new Date(log.timestamp / 1000), 'MMM dd, HH:mm:ss')}
                        </TableCell>
                        <TableCell>{getSeverityBadge(log.error_severity)}</TableCell>
                        <CollapsibleTrigger asChild>
                          <TableCell className="group">
                            <div className="flex items-center justify-between">
                              <span className="truncate max-w-[600px]">{log.event_message}</span>
                              <ChevronDown className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </TableCell>
                        </CollapsibleTrigger>
                      </TableRow>
                      <CollapsibleContent asChild>
                        <TableRow>
                          <TableCell colSpan={3} className="bg-muted/50">
                            <div className="p-4 space-y-2">
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="font-semibold">ID:</span> {log.id}
                                </div>
                                <div>
                                  <span className="font-semibold">Identifier:</span> {log.identifier}
                                </div>
                              </div>
                              <div className="text-sm">
                                <span className="font-semibold">Full Message:</span>
                                <pre className="mt-2 p-2 bg-background rounded border overflow-x-auto text-xs">
                                  {log.event_message}
                                </pre>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </CollapsibleContent>
                    </>
                  </Collapsible>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
